/**
 * useAutoQueue.js
 *
 * Monitors queue depth during radio mode and triggers the vector recommendation
 * engine when fewer than 3 tracks remain. Also handles periodic behavior sync
 * (every 5 minutes) so the taste vector stays fresh.
 */
import { useCallback, useRef } from 'react';
import logger from '../services/logger';
import api from '../services/api';
import { getAndClearPendingActions } from '../utils/basicBehaviorLogger';

export function useAutoQueue(state, dispatch, refs) {
  const {
    radioModeRef,
    addTracksToPlaylist,
  } = refs;

  const isGeneratingRef = useRef(false);
  const lastGeneratedRef = useRef(0);
  const syncIntervalRef = useRef(null);
  const generateMoreRef = useRef(null); // always-current ref — avoids use-before-define in checkQueue dep array

  /**
   * Called from setProgress on every tick.
   * Checks if the queue is running low and triggers generation.
   */
  const checkQueue = useCallback(() => {
    if (!radioModeRef.current?.isActive) return;
    if (isGeneratingRef.current) return;
    if (Date.now() - lastGeneratedRef.current < 30000) return; // 30s debounce

    const remaining = state.playlist.tracks.length - state.currentTrackIndex - 1;
    if (remaining < 5) {
      generateMoreRef.current?.(); // call via ref — always the latest generateMore
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.playlist.tracks.length, state.currentTrackIndex]);

  /**
   * POST /api/recommendations/generate
   * Fetches new tracks from the vector engine and adds them to the playlist.
   */
  const generateMore = useCallback(async () => {
    if (isGeneratingRef.current) return;
    isGeneratingRef.current = true;
    lastGeneratedRef.current = Date.now();

    try {
      // Build playlist URIs for dedup
      const playlistUris = state.playlist.tracks
        .filter(t => t && t.uri)
        .map(t => t.uri);

      const currentTrack = state.playlist.tracks[state.currentTrackIndex];

      // Original playlist artists: all artists from user-loaded tracks (not engine-added).
      // Tracks added by the recommendation engine have _source === 'vector_engine' and are excluded.
      // These seed Last.fm artist.getSimilar for the full playlist context.
      const originalArtists = [];
      const seenOriginal = new Set();
      for (const t of state.playlist.tracks) {
        if (!t || t._source === 'vector_engine') continue;
        const artistStr = typeof t.artists === 'string'
          ? t.artists
          : (t.artists?.map(a => a.name || a).join(', ') || '');
        for (const name of artistStr.split(', ').filter(Boolean)) {
          if (!seenOriginal.has(name)) { seenOriginal.add(name); originalArtists.push(name); }
        }
      }

      // ── Playlist snapshot (debug) ─────────────────────────────────────────
      const remaining = state.playlist.tracks.length - state.currentTrackIndex - 1;
      const snapshotLines = state.playlist.tracks.map((t, i) => {
        const marker = i < state.currentTrackIndex ? '✓' : i === state.currentTrackIndex ? '►' : ' ';
        const artistStr = typeof t?.artists === 'string' ? t.artists
          : (t?.artists?.map(a => a.name || a).join(', ') || 'Unknown');
        const src = t?._source === 'vector_engine' ? '[engine]' : '[user]  ';
        return `  [${i}] ${marker} ${src} ${artistStr} – ${t?.name || '?'}`;
      }).join('\n');
      logger.debug(
        `[AutoQueue] Playlist snapshot — ${state.playlist.tracks.length} tracks | ` +
        `idx=${state.currentTrackIndex} | remaining=${remaining}\n${snapshotLines}`
      );
      logger.debug(`[AutoQueue] Seeds sent to backend (${originalArtists.length}): ${originalArtists.join(', ')}`);
      // ─────────────────────────────────────────────────────────────────────

      const result = await api.generateRecommendations({
        current_track: currentTrack ? {
          uri: currentTrack.uri,
          name: currentTrack.name,
          artists: currentTrack.artists,
        } : null,
        playlist_uris: playlistUris,
        playlist_context: { original_artists: originalArtists },
        count: 9,
        platform: state.platform || 'spotify',
      });

      if (result.success && result.tracks && result.tracks.length > 0) {
        // Normalize tracks for playlist format
        const newTracks = result.tracks.map(t => ({
          uri: t.uri,
          name: t.name,
          artists: typeof t.artists === 'string' ? t.artists
            : t.artists?.map(a => a.name).join(', ') || 'Unknown',
          album: t.album || {},
          popularity: t.popularity || 50,
          _source: 'vector_engine',
          _similarity: t.similarity,
        }));

        addTracksToPlaylist(newTracks);
        logger.success(`Auto-queue: added ${newTracks.length} tracks (${result.insufficient ? 'partial' : 'full'})`);
      } else {
        logger.warn('Auto-queue: no tracks returned', result.error || '');
      }
    } catch (error) {
      logger.error('Auto-queue generation failed:', error);
    } finally {
      isGeneratingRef.current = false;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.playlist.tracks, state.currentTrackIndex, state.platform, addTracksToPlaylist]);

  // Keep ref current on every render so checkQueue always calls the latest generateMore
  generateMoreRef.current = generateMore;

  /**
   * POST /api/recommendations/sync-behavior
   * Flush pending behavior actions from localStorage to the backend.
   */
  const syncBehavior = useCallback(async () => {
    try {
      const pending = getAndClearPendingActions();
      if (pending.length === 0) return;

      logger.debug(`Syncing ${pending.length} behavior actions to backend`);
      const result = await api.syncBehavior(pending);

      if (result.success && result.profile_updated) {
        logger.debug(`Taste profile updated (total interactions: ${result.total_interactions})`);
      }
    } catch (error) {
      logger.error('Behavior sync failed:', error);
    }
  }, []);

  /**
   * Start 5-minute sync interval for behavior data.
   */
  const startSyncInterval = useCallback(() => {
    if (syncIntervalRef.current) return;
    syncIntervalRef.current = setInterval(() => {
      syncBehavior();
    }, 5 * 60 * 1000); // 5 minutes
    logger.debug('Auto-queue: behavior sync interval started (5 min)');
  }, [syncBehavior]);

  /**
   * Stop sync interval.
   */
  const stopSyncInterval = useCallback(() => {
    if (syncIntervalRef.current) {
      clearInterval(syncIntervalRef.current);
      syncIntervalRef.current = null;
      logger.debug('Auto-queue: behavior sync interval stopped');
    }
  }, []);

  /**
   * PUT /api/recommendations/profile/discovery
   */
  const setDiscoveryLevel = useCallback(async (level) => {
    try {
      const result = await api.updateDiscoveryLevel(level);
      if (result.success) {
        logger.debug(`Discovery level set to ${level}`);
      }
    } catch (error) {
      logger.error('Set discovery level failed:', error);
    }
  }, []);

  /**
   * POST /api/recommendations/profile/import
   */
  const importProfile = useCallback(async () => {
    try {
      logger.debug('Importing Spotify profile for taste vector bootstrap...');
      const result = await api.importPlatformProfile('spotify');
      if (result.success) {
        logger.success(`Imported ${result.tracks_imported} tracks into taste profile`);
      }
      return result;
    } catch (error) {
      logger.error('Profile import failed:', error);
      return { success: false, error: error.message };
    }
  }, []);

  return {
    checkQueue,
    generateMore,
    syncBehavior,
    startSyncInterval,
    stopSyncInterval,
    setDiscoveryLevel,
    importProfile,
  };
}
